/* -----------------------------------------------------------------------------
 * SmartEvaluate — RFP Client Inputs Tab
 * ---------------------------------------------------------------------------
 * Renders the "Client Inputs" tab for assessments whose `submission_type`
 * equals "rfp". Replaces the guided questionnaire summary with four panels:
 *
 *   1. PRIVA Analysis status (above everything else)
 *   2. Client Details
 *   3. RFP Documents (with Add / Remove actions)
 *   4. Additional Notes / Context
 *
 * The guided questionnaire summary is intentionally not shown here — those
 * sections will be populated only AFTER PRIVA extraction runs on the uploaded
 * documents.
 *
 * Important boundaries (per product spec):
 *   - Do NOT call Claude / PRIVA from this component. The "Run PRIVA Analysis"
 *     button is a placeholder and stays disabled.
 *   - Do NOT mutate insights / recommendations / questionnaire answers here.
 *   - This is strictly the Client Inputs display + document management layer.
 *
 * Data shape expected on the assessment object (all optional, gracefully
 * defaults are applied if missing):
 *
 *   assessment.submission_type        // "rfp"
 *   assessment.rfp_client_info        // { company_name, contact_name, role,
 *                                     //   email, phone, industry, country }
 *   assessment.rfp_documents          // [{ id, name, ext, size,
 *                                     //    uploaded_at, uploaded_by }]
 *   assessment.rfp_notes              // string
 *   assessment.priva_analysis         // { status: "not_run" | "running"
 *                                     //   | "complete", last_run_at }
 * -------------------------------------------------------------------------- */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles,
  Building2,
  FileText,
  FileSpreadsheet,
  Presentation,
  Upload,
  Trash2,
  Download,
  Eye,
  Info,
  StickyNote,
  Save,
  Pencil,
  AlertCircle,
  Loader2,
  RotateCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  addRfpDocuments,
  removeRfpDocument,
  patchRfpSubmission,
  rfpDocumentDownloadUrl,
  runRfpPrivaAnalysis,
} from "../lib/rfpApi";
import { questionnaireXlsxUrl } from "../lib/adminApi";
import PrivaExtractedIntelligence from "./RfpPrivaIntelligence";

const ACCEPTED_EXTENSIONS = ["pdf", "docx", "pptx", "xlsx"];
const ACCEPT_ATTR =
  ".pdf,.docx,.pptx,.xlsx," +
  "application/pdf," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function fileExt(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function iconForExt(ext) {
  if (ext === "xlsx") return FileSpreadsheet;
  if (ext === "pptx") return Presentation;
  return FileText;
}

function prettyType(ext) {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return "PDF";
  if (e === "docx") return "Word";
  if (e === "pptx") return "PowerPoint";
  if (e === "xlsx") return "Excel";
  return e ? e.toUpperCase() : "FILE";
}

const PRIVA_STATUS_LABELS = {
  not_run: "Not Run",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

/* -------------------------------------------------------------------------- */
/*  PRIVA Analysis panel                                                       */
/* -------------------------------------------------------------------------- */
function PrivaAnalysisPanel({
  assessmentId,
  status,
  lastRunAt,
  modelUsed,
  promptVersion,
  warnings,
  errorMessage,
  onRunStarted,
}) {
  const isRunning = status === "running";
  const isCompleted = status === "completed";
  const isFailed = status === "failed";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  let label;
  if (isRunning) label = "Analyzing…";
  else if (isCompleted) label = "Re-run PRIVA Analysis";
  else if (isFailed) label = "Retry PRIVA Analysis";
  else label = "Run PRIVA Analysis";

  const statusLabel =
    PRIVA_STATUS_LABELS[status] ||
    (status ? status[0].toUpperCase() + status.slice(1) : "Not Run");

  const triggerRun = async () => {
    if (!assessmentId) return;
    setBusy(true);
    try {
      await runRfpPrivaAnalysis(assessmentId);
      toast.success("PRIVA analysis started. This may take 30-90 seconds.");
      onRunStarted?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to start PRIVA analysis.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const handleClick = () => {
    if (isRunning) return;
    if (isCompleted) {
      setConfirmOpen(true);
    } else {
      triggerRun();
    }
  };

  return (
    <div
      data-testid="rfp-priva-analysis-panel"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-sm bg-[#F3E8FF] border border-[#6D28D9]/15 flex items-center justify-center shrink-0">
              <Sparkles
                className="w-4 h-4 text-[#6D28D9]"
                strokeWidth={1.75}
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  className="text-sm font-semibold text-[#111827] uppercase tracking-wider"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  PRIVA Analysis
                </h3>
                <span
                  data-testid="rfp-priva-status-badge"
                  className={[
                    "inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 border",
                    !status || status === "not_run"
                      ? "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]"
                      : isRunning
                      ? "bg-[#FEF3C7] text-[#92400E] border-[#92400E]/15"
                      : isCompleted
                      ? "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15"
                      : "bg-[#FEE2E2] text-[#991B1B] border-[#991B1B]/15",
                  ].join(" ")}
                >
                  Status: {statusLabel}
                </span>
                {isCompleted && promptVersion && (
                  <span className="text-[10px] text-[#9CA3AF]">
                    Prompt {promptVersion} · {modelUsed || "Claude"}
                  </span>
                )}
              </div>
              <p
                data-testid="rfp-priva-helper-text"
                className="mt-1.5 text-[12px] text-[#4B5563] leading-relaxed max-w-2xl"
              >
                Run PRIVA Analysis to extract RFP requirements, warehouse scope,
                integrations, assumptions, risks, KPIs, and bid inputs from the
                uploaded documents.
              </p>
              {lastRunAt && (
                <p className="mt-1 text-[11px] text-[#9CA3AF]">
                  Last run: {formatDate(lastRunAt)}
                </p>
              )}
            </div>
          </div>

          <div className="shrink-0">
            <Button
              data-testid="rfp-run-priva-btn"
              disabled={isRunning || busy || !assessmentId}
              onClick={handleClick}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-10 px-5 text-sm font-semibold rounded-sm transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#8B5CF6]"
            >
              {isRunning ? (
                <Loader2 className="mr-2 w-4 h-4 animate-spin" strokeWidth={1.75} />
              ) : isCompleted ? (
                <RotateCw className="mr-2 w-4 h-4" strokeWidth={1.75} />
              ) : (
                <Sparkles className="mr-2 w-4 h-4" strokeWidth={1.75} />
              )}
              {label}
            </Button>
          </div>
        </div>

        {isFailed && errorMessage && (
          <div
            data-testid="rfp-priva-error"
            className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-sm px-3.5 py-2.5"
          >
            <AlertCircle
              className="w-4 h-4 text-red-600 mt-0.5 shrink-0"
              strokeWidth={1.75}
            />
            <span className="text-[12px] text-red-700 leading-relaxed break-words">
              {errorMessage}
            </span>
          </div>
        )}

        {Array.isArray(warnings) && warnings.length > 0 && isCompleted && (
          <div
            data-testid="rfp-priva-warnings"
            className="mt-4 bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-sm px-3.5 py-2.5"
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle
                className="w-3.5 h-3.5 text-[#B45309]"
                strokeWidth={1.75}
              />
              <span className="text-[11px] font-semibold text-[#92400E] uppercase tracking-wider">
                {warnings.length} parsing warning{warnings.length === 1 ? "" : "s"}
              </span>
            </div>
            <ul className="ml-5 list-disc text-[12px] text-[#92400E] space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Re-run confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="rfp-priva-rerun-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Re-run PRIVA Analysis?</DialogTitle>
            <DialogDescription className="text-[13px] text-[#4B5563] leading-relaxed">
              Re-running PRIVA Analysis will replace the previous extracted RFP
              analysis. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              data-testid="rfp-priva-rerun-cancel-btn"
              onClick={() => setConfirmOpen(false)}
              className="rounded-sm h-9 px-4 text-xs font-medium border-[#D1D5DB] text-[#374151] hover:bg-[#F8FAFC]"
            >
              Cancel
            </Button>
            <Button
              data-testid="rfp-priva-rerun-confirm-btn"
              onClick={() => {
                setConfirmOpen(false);
                triggerRun();
              }}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-9 px-4 text-xs font-semibold rounded-sm"
            >
              Yes, Re-run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Client Details panel                                                       */
/* -------------------------------------------------------------------------- */
const CLIENT_FIELDS = [
  { key: "company_name", label: "Company Name" },
  { key: "contact_name", label: "Contact Name" },
  { key: "role", label: "Job Title / Role" },
  { key: "email", label: "Email Address" },
  { key: "phone", label: "Phone Number" },
  { key: "industry", label: "Industry" },
  { key: "country", label: "Country / Region" },
];

function ClientDetailsPanel({ clientInfo }) {
  const info = clientInfo || {};
  return (
    <div
      data-testid="rfp-client-details-panel"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#E5E7EB] flex items-center gap-2">
        <Building2 className="w-4 h-4 text-[#025979]" strokeWidth={1.5} />
        <h3
          className="text-sm font-semibold text-[#111827] uppercase tracking-wider"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Client Details
        </h3>
      </div>
      <dl className="px-5 py-4 sm:px-6 sm:py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {CLIENT_FIELDS.map((f) => {
          const value = (info[f.key] || "").toString().trim();
          return (
            <div
              key={f.key}
              data-testid={`rfp-client-detail-${f.key}`}
              className="min-w-0"
            >
              <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
                {f.label}
              </dt>
              <dd
                className={[
                  "mt-1 text-sm break-words",
                  value ? "text-[#1F2937]" : "text-[#9CA3AF] italic",
                ].join(" ")}
              >
                {value || "—"}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  RFP Documents panel                                                        */
/* -------------------------------------------------------------------------- */
function DocumentsPanel({ assessmentId, documents, onAdd, onRemove }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleAddClick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleFilesPicked = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (picked.length === 0) return;

    const valid = [];
    const rejected = [];
    picked.forEach((f) => {
      const ext = fileExt(f.name);
      if (ACCEPTED_EXTENSIONS.includes(ext)) valid.push(f);
      else rejected.push(f.name);
    });

    if (rejected.length > 0) {
      toast.error(
        `Unsupported file type: ${rejected.join(
          ", "
        )}. Allowed formats: PDF, DOCX, PPTX, XLSX.`
      );
    }
    if (valid.length === 0) return;

    setBusy(true);
    try {
      const resp = await addRfpDocuments(assessmentId, valid);
      const added = resp?.documents || [];
      onAdd(added);
      toast.success(
        `${added.length} document${added.length === 1 ? "" : "s"} uploaded.`
      );
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to upload documents.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (doc) => {
    if (documents.length <= 1) {
      toast.warning(
        "At least one RFP or requirements document should remain before running PRIVA Analysis."
      );
      return;
    }
    const confirmed = window.confirm(
      `Remove "${doc.name}"? This document will no longer be available for PRIVA Analysis.`
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await removeRfpDocument(assessmentId, doc.id);
      onRemove(doc.id);
      toast.success("Document removed.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to remove document.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="rfp-documents-panel"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#025979]" strokeWidth={1.5} />
          <h3
            className="text-sm font-semibold text-[#111827] uppercase tracking-wider"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            RFP Documents
          </h3>
          <span
            data-testid="rfp-documents-count"
            className="text-[11px] font-medium text-[#6B7280]"
          >
            ({documents.length})
          </span>
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="hidden"
            data-testid="rfp-documents-file-input"
            onChange={handleFilesPicked}
          />
          <Button
            data-testid="rfp-add-documents-btn"
            onClick={handleAddClick}
            disabled={busy || !assessmentId}
            className="bg-[#025979] hover:bg-[#03738C] text-white h-9 px-4 text-xs font-semibold rounded-sm transition-colors shadow-sm disabled:opacity-50"
          >
            <Upload className="mr-1.5 w-3.5 h-3.5" strokeWidth={1.75} />
            {busy ? "Uploading…" : "Add Documents"}
          </Button>
        </div>
      </div>

      {documents.length === 0 ? (
        <div
          data-testid="rfp-documents-empty"
          className="px-5 py-8 sm:px-6 text-center"
        >
          <p className="text-sm text-[#6B7280]">
            No documents have been added yet.
          </p>
          <p className="mt-1 text-xs text-[#9CA3AF]">
            Upload at least one RFP or requirements document to enable PRIVA
            Analysis.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table
            data-testid="rfp-documents-table"
            className="w-full text-sm"
          >
            <thead className="bg-[#F8FAFC] text-[#6B7280]">
              <tr>
                <th className="text-left text-[11px] font-medium uppercase tracking-wider px-4 py-2.5">
                  File Name
                </th>
                <th className="text-left text-[11px] font-medium uppercase tracking-wider px-4 py-2.5">
                  Type
                </th>
                <th className="text-left text-[11px] font-medium uppercase tracking-wider px-4 py-2.5">
                  Size
                </th>
                <th className="text-left text-[11px] font-medium uppercase tracking-wider px-4 py-2.5">
                  Uploaded
                </th>
                <th className="text-left text-[11px] font-medium uppercase tracking-wider px-4 py-2.5">
                  By
                </th>
                <th className="text-right text-[11px] font-medium uppercase tracking-wider px-4 py-2.5">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {documents.map((doc) => {
                const Icon = iconForExt(doc.ext);
                const downloadUrl = assessmentId
                  ? rfpDocumentDownloadUrl(assessmentId, doc.id)
                  : null;
                const canDownload = !!downloadUrl;
                return (
                  <tr
                    key={doc.id || doc.name}
                    data-testid={`rfp-document-row-${doc.id || doc.name}`}
                    className="hover:bg-[#F8FAFC] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-sm bg-[#E0F2FE] flex items-center justify-center shrink-0">
                          <Icon
                            className="w-3.5 h-3.5 text-[#025979]"
                            strokeWidth={1.75}
                          />
                        </div>
                        <span className="font-medium text-[#1F2937] truncate max-w-[280px]">
                          {doc.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#4B5563]">
                      {prettyType(doc.ext)}
                    </td>
                    <td className="px-4 py-3 text-[#4B5563]">
                      {formatBytes(doc.size)}
                    </td>
                    <td className="px-4 py-3 text-[#4B5563]">
                      {formatDate(doc.uploaded_at)}
                    </td>
                    <td className="px-4 py-3 text-[#4B5563]">
                      {doc.uploaded_by || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <a
                          data-testid={`rfp-document-view-${doc.id || doc.name}`}
                          href={canDownload ? downloadUrl : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (!canDownload) {
                              e.preventDefault();
                              toast.info("Document is not yet available.");
                            }
                          }}
                          className={[
                            "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-sm transition-colors",
                            canDownload
                              ? "text-[#025979] hover:text-[#03738C]"
                              : "text-[#9CA3AF] cursor-not-allowed",
                          ].join(" ")}
                          title={canDownload ? "View document" : "Preview not available"}
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
                          View
                        </a>
                        <a
                          data-testid={`rfp-document-download-${doc.id || doc.name}`}
                          href={canDownload ? downloadUrl : undefined}
                          download={doc.name}
                          onClick={(e) => {
                            if (!canDownload) {
                              e.preventDefault();
                              toast.info("Download not available yet.");
                            }
                          }}
                          className={[
                            "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-sm transition-colors",
                            canDownload
                              ? "text-[#025979] hover:text-[#03738C]"
                              : "text-[#9CA3AF] cursor-not-allowed",
                          ].join(" ")}
                          title={canDownload ? "Download document" : "Download not available"}
                        >
                          <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
                          Download
                        </a>
                        <button
                          type="button"
                          data-testid={`rfp-document-remove-${doc.id || doc.name}`}
                          onClick={() => handleRemove(doc)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 px-2 py-1 rounded-sm transition-colors"
                          title="Remove document"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Additional Notes / Context panel                                           */
/* -------------------------------------------------------------------------- */
function NotesPanel({ assessmentId, notes, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes || "");
  const [saving, setSaving] = useState(false);

  const hasNotes = (notes || "").trim().length > 0;

  const handleSave = async () => {
    if (!assessmentId) {
      onSave(draft);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await patchRfpSubmission(assessmentId, { rfp_notes: draft });
      onSave(draft);
      setEditing(false);
      toast.success("Notes updated.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to save notes.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(notes || "");
    setEditing(false);
  };

  return (
    <div
      data-testid="rfp-notes-panel"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-[#025979]" strokeWidth={1.5} />
          <h3
            className="text-sm font-semibold text-[#111827] uppercase tracking-wider"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Additional Notes / Context
          </h3>
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            data-testid="rfp-notes-edit-btn"
            onClick={() => {
              setDraft(notes || "");
              setEditing(true);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#025979] hover:text-[#03738C] transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
            Edit
          </button>
        )}
      </div>

      <div className="px-5 py-4 sm:px-6 sm:py-5">
        {editing ? (
          <div className="space-y-3">
            <Textarea
              data-testid="rfp-notes-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              placeholder="Add any extra context that PRIVA and consultants should consider when reviewing the RFP."
              className="rounded-sm text-sm border-[#D1D5DB] text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#025979] focus:ring-[#025979] transition-colors"
            />
            <div className="flex items-center gap-2 justify-end">
              <Button
                data-testid="rfp-notes-cancel-btn"
                variant="outline"
                onClick={handleCancel}
                className="rounded-sm h-9 px-4 text-xs font-medium border-[#D1D5DB] text-[#374151] hover:bg-[#F8FAFC] transition-colors"
              >
                Cancel
              </Button>
              <Button
                data-testid="rfp-notes-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-[#025979] hover:bg-[#03738C] text-white h-9 px-4 text-xs font-semibold rounded-sm transition-colors shadow-sm disabled:opacity-50"
              >
                <Save className="mr-1.5 w-3.5 h-3.5" strokeWidth={1.75} />
                {saving ? "Saving…" : "Save Notes"}
              </Button>
            </div>
          </div>
        ) : (
          <p
            data-testid="rfp-notes-content"
            className={[
              "text-sm leading-relaxed whitespace-pre-wrap break-words",
              hasNotes ? "text-[#1F2937]" : "text-[#9CA3AF] italic",
            ].join(" ")}
          >
            {hasNotes
              ? notes
              : "No additional notes or context were submitted with this RFP."}
          </p>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Top-level RfpClientInputsTab                                               */
/* -------------------------------------------------------------------------- */
export default function RfpClientInputsTab({
  assessment,
  onChange,
  onRefresh,
  canEditNotes = true,
}) {
  // Stable defaults so the UI never crashes on partial / freshly-created RFP
  // records.
  const assessmentId = assessment?.id;
  const clientInfo = assessment?.rfp_client_info || {};
  const initialDocuments = useMemo(
    () =>
      (Array.isArray(assessment?.rfp_documents) ? assessment.rfp_documents : [])
        .filter((d) => !d.is_deleted),
    [assessment?.rfp_documents]
  );
  const initialNotes = assessment?.rfp_notes || clientInfo?.notes || "";
  const priva = assessment?.priva_analysis || { status: "not_run" };
  const extractionResult = assessment?.rfp_extraction_result;

  // Local state so Add/Remove + notes updates reflect immediately while we
  // POST/PATCH to the backend. `onChange` is fired so the parent can also
  // refresh its cached assessment if needed.
  const [documents, setDocuments] = useState(initialDocuments);
  const [notes, setNotes] = useState(initialNotes);

  // Re-sync local state whenever the parent reloads the assessment (e.g.
  // after a PRIVA run completes and rfp_documents/rfp_notes are unchanged
  // but priva_analysis flips to "completed").
  useEffect(() => {
    setDocuments(initialDocuments);
  }, [initialDocuments]);
  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  // Poll while PRIVA is running so the consultant sees "completed" as soon
  // as the background task finishes. Stops automatically when status flips
  // or the component unmounts.
  const pollTimerRef = useRef(null);
  useEffect(() => {
    if (priva.status !== "running" || !onRefresh || !assessmentId) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }
    pollTimerRef.current = setInterval(() => {
      onRefresh();
    }, 5000);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [priva.status, onRefresh, assessmentId]);

  const handleAddDocs = (newDocs) => {
    setDocuments((prev) => {
      const next = [...prev, ...newDocs];
      onChange?.({ rfp_documents: next });
      return next;
    });
  };

  const handleRemoveDoc = (docId) => {
    setDocuments((prev) => {
      const next = prev.filter((d) => (d.id || d.name) !== docId);
      onChange?.({ rfp_documents: next });
      return next;
    });
  };

  const handleSaveNotes = (value) => {
    setNotes(value);
    onChange?.({ rfp_notes: value });
  };

  const handlePrivaRunStarted = () => {
    // Refresh immediately so the panel flips to "running" without waiting
    // for the next poll tick.
    onRefresh?.();
  };

  return (
    <div
      className="space-y-4"
      data-testid="rfp-client-inputs-tab"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3 flex-1 min-w-0">
          <Info
            className="w-4 h-4 text-[#6D28D9] mt-0.5 shrink-0"
            strokeWidth={1.5}
          />
          <span className="text-[12px] text-[#4B5563] leading-relaxed">
            This is a <strong>SmartBid RFP &amp; Requirements Analysis</strong> intake.
            Structured bid inputs will appear here after PRIVA extracts
            requirements, scope, assumptions, risks, KPIs, and transformation
            inputs from the uploaded documents.
          </span>
        </div>
        {priva.status === "completed" && (assessment?.conversion_status || "not_started") !== "converted" && (
          <a
            data-testid="export-questionnaire-xlsx"
            href={questionnaireXlsxUrl(assessmentId)}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex items-center text-xs bg-white border border-[#025979] text-[#025979] hover:bg-[#E6F1F5] rounded-sm px-3 py-1.5 transition-colors self-start shrink-0"
            title="Right-click and choose 'Save link as…' if the browser blocks the download."
          >
            <FileSpreadsheet className="w-3 h-3 mr-1.5" /> Export Questionnaire (right click)
          </a>
        )}
      </div>

      <PrivaAnalysisPanel
        assessmentId={assessmentId}
        status={priva.status}
        lastRunAt={priva.last_run_at || priva.run_completed_at}
        modelUsed={priva.model_used}
        promptVersion={priva.prompt_version}
        warnings={priva.warnings}
        errorMessage={priva.error_message}
        onRunStarted={handlePrivaRunStarted}
      />

      <ClientDetailsPanel clientInfo={clientInfo} />

      <DocumentsPanel
        assessmentId={assessmentId}
        documents={documents}
        onAdd={handleAddDocs}
        onRemove={handleRemoveDoc}
      />

      <NotesPanel
        assessmentId={assessmentId}
        notes={notes}
        canEdit={canEditNotes}
        onSave={handleSaveNotes}
      />

      {priva.status === "completed" && extractionResult && (
        <PrivaExtractedIntelligence
          result={extractionResult}
          assessmentId={assessmentId}
          reviewStatus={assessment?.extraction_review_status || "not_reviewed"}
          conversionStatus={assessment?.conversion_status || "not_started"}
          onReviewUpdated={() => onRefresh?.()}
          onConverted={() => onRefresh?.()}
        />
      )}
    </div>
  );
}
